import React, { useState, useEffect } from 'react';
import { X, Link2, Check, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../services/authService';
import formationService from '../../services/formationService';
import './AssignFormationsDialog.css';

const AssignFormationsDialog = ({ isOpen, onClose, onAssigned, consommable }) => {
  const { t } = useTranslation();
  const [formations, setFormations] = useState([]);
  const [selectedFormations, setSelectedFormations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && consommable) {
      fetchFormations();
      // Pre-select current formations
      setSelectedFormations(consommable.formations || []);
      setError(null);
      setSearchTerm('');
    }
  }, [isOpen, consommable]);

  const fetchFormations = async () => {
    try {
      const response = await formationService.getSessionOptions();
      setFormations(response);
    } catch (err) {
      console.error('Error fetching formations:', err);
      setError(t('consommables.assignDialog.errors.loadFormations'));
    }
  };

  const handleToggleFormation = (formationId) => {
    setSelectedFormations(prev => {
      if (prev.includes(formationId)) {
        return prev.filter(id => id !== formationId);
      }
      return [...prev, formationId];
    });
  };

  const handleSelectAll = () => {
    const allIds = filteredFormations.map(f => f.id);
    const allSelected = allIds.every(id => selectedFormations.includes(id));
    
    if (allSelected) {
      // Deselect all visible
      setSelectedFormations(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      // Select all visible
      setSelectedFormations(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  const handleSubmit = async () => {
    if (!consommable) return;

    setLoading(true);
    setError(null);

    try {
      await apiClient.post(`/consommables/${consommable.id}/assign_formations/`, {
        formation_ids: selectedFormations
      });
      
      onAssigned();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || t('consommables.assignDialog.errors.assign'));
    } finally {
      setLoading(false);
    }
  };

  const filteredFormations = formations.filter(f => 
    f.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedCount = selectedFormations.length;

  if (!isOpen || !consommable) return null;

  return (
    <div className="dialog-overlay ui-dialog-overlay" onClick={(e) => e.target === e.currentTarget && !loading && onClose()}>
      <div className="dialog-container assign-dialog ui-dialog-container">
        <div className="dialog-header ui-dialog-header">
          <div className="dialog-title ui-dialog-title">
            <Link2 size={24} />
            <h2>{t('consommables.assignDialog.title')}</h2>
          </div>
          <button className="dialog-close" onClick={onClose} disabled={loading}>
            <X size={20} />
          </button>
        </div>

        <div className="dialog-subtitle">
          <strong>{consommable.name}</strong>
        </div>

        {error && (
          <div className="dialog-error">
            {error}
          </div>
        )}

        <div className="search-bar">
          <Search size={18} />
          <input
            type="text"
            placeholder={t('consommables.assignDialog.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ui-input"
          />
        </div>

        <div className="selection-info">
          <span>{t('consommables.assignDialog.selectedCount', { count: selectedCount })}</span>
          <button className="select-all-btn" onClick={handleSelectAll}>
            {t('consommables.assignDialog.selectAllToggle')}
          </button>
        </div>

        <div className="formations-list">
          {filteredFormations.length === 0 ? (
            <div className="empty-formations">
              {t('consommables.assignDialog.empty')}
            </div>
          ) : (
            filteredFormations.map(formation => {
              const isSelected = selectedFormations.includes(formation.id);
              return (
                <div
                  key={formation.id}
                  className={`formation-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleToggleFormation(formation.id)}
                >
                  <div className="formation-checkbox">
                    {isSelected && <Check size={16} />}
                  </div>
                  <div className="formation-info">
                    <span className="formation-title">{formation.title}</span>
                    <span className="formation-duration">{t('common.daysCount', { count: formation.duration_days })}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="dialog-actions ui-dialog-actions">
          <button
            type="button"
            className="btn-secondary ui-btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn-primary ui-btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? t('common.saving') : (
              <>
                <Link2 size={18} />
                {t('consommables.assignDialog.assignButton', { count: selectedCount })}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignFormationsDialog;
